import { useNetwork } from '@vueuse/core';
import type { ApiErrorModel } from '~/common/models/api.models.ts';
import { usePaymentError } from './usePaymentError';
import { usePaymentInitReport } from './usePaymentInitReport';
import { PAYMENT_INIT_ACTION } from '../common/constants/paymentInit';
import { rememberPaymentMethod } from '../utils/paymentMethodStorage';

const PREFETCH_ATTEMPTS = 3;
const PREFETCH_RETRY_DELAY = 1500;

const cachedBaToken = ref<string | null>(null);
const cachedBaTokenAmount = ref<number | null>(null);
let pendingBaToken: Promise<string> | null = null;
let pendingBaTokenAmount: number | null = null;

function dropBaToken(): void {
  cachedBaToken.value = null;
  cachedBaTokenAmount.value = null;
}

function hasBaTokenFor(amount: number): boolean {
  return !!cachedBaToken.value && cachedBaTokenAmount.value === amount;
}

export function useLagoPayPalToken() {
  const store = useKegelAnalyticStore();
  const API = useKegelApi();
  const { $logger } = useNuxtApp();
  const { gatewayType } = useHelpers();

  function requestBaToken(amount: number): Promise<string> {
    if (!pendingBaToken || pendingBaTokenAmount !== amount) {
      pendingBaTokenAmount = amount;
      pendingBaToken = API.paypalInit(store.userUuid, amount)
        .then(({ ba_token }) => ba_token!)
        .finally(() => {
          pendingBaToken = null;
          pendingBaTokenAmount = null;
        });
    }

    return pendingBaToken;
  }

  async function warmBaToken(amount: number): Promise<void> {
    if (hasBaTokenFor(amount)) return;

    for (let attempt = 1; attempt <= PREFETCH_ATTEMPTS; attempt++) {
      try {
        cachedBaToken.value = await requestBaToken(amount);
        cachedBaTokenAmount.value = amount;
        return;
      } catch (error: unknown) {
        if (attempt === PREFETCH_ATTEMPTS) {
          $logger.error(error as Error, {
            tags: { flow: 'payment', gateway: gatewayType.value, step: 'paypal_ba_token_prefetch' },
            extra: { attempts: attempt },
          });
          return;
        }
        await new Promise(resolve => setTimeout(resolve, PREFETCH_RETRY_DELAY * attempt));
      }
    }
  }

  async function takeBaToken(amount: number): Promise<string> {
    if (hasBaTokenFor(amount)) return cachedBaToken.value!;

    dropBaToken();
    return await requestBaToken(amount);
  }

  return { warmBaToken, takeBaToken, dropBaToken };
}

interface UseLagoPayPalOptions {
  chargebeeId: () => string;
  discountPrice: () => number;
  roundedPrice: () => number;
  upsale: () => boolean;
  requestPayerEmail: () => boolean;
  onSuccess: () => void;
  onUnavailable: () => void;
  onAvailable: () => void;
}

export function useLagoPayPal(options: UseLagoPayPalOptions) {
  const gatewayStore = useKegelGatewayStore();
  const store = useKegelAnalyticStore();
  const API = useKegelApi();
  const dialogStore = useDialogStore();
  const { openError } = useAllModals();
  const { $paymentMethods } = useNuxtApp();
  const { setUserProperty } = useMixpanel();
  const { reportPaymentError } = usePaymentError();
  const { track } = useEventsDispatcher();
  const { roundNumber, paypalGatewayType } = useHelpers();
  const { buyUpsale } = payments();

  const { warmBaToken, takeBaToken, dropBaToken } = useLagoPayPalToken();
  const { reportPaymentInit } = usePaymentInitReport();
  const { isOnline } = useNetwork();
  const { registerPayerWithoutEmail, setEmailAfterPpPayment } = useEmail();

  const isLoading = ref(false);
  const isAuthorizing = ref(false);
  const payPalStartTime = ref(0);
  const buttonKey = ref(0);
  const hasInitError = ref(false);

  async function prefetchBaToken(): Promise<void> {
    const amount = options.roundedPrice();
    if (gatewayStore.provider !== 'round' || !amount) return;

    await warmBaToken(amount);
  }

  watch(() => options.roundedPrice(), () => {
    dropBaToken();
    void prefetchBaToken();
  });

  async function getClientId(): Promise<string> {
    return gatewayStore.lagoPaypalClientId;
  }

  function trackPayPalStart(): void {
    payPalStartTime.value = performance.now();
    track('Authorization_Start', { type: $paymentMethods.paypal, gateway: paypalGatewayType.value });
    track('Check-out Started', { type: $paymentMethods.paypal, gateway: paypalGatewayType.value });
  }

  function handleClick(): void {
    isAuthorizing.value = true;
    setUserProperty({ gateway: 'paypal_express_checkout' });
  }

  function showAuthorizingLoader(): void {
    isAuthorizing.value = true;
  }

  function hideAuthorizingLoader(): void {
    isAuthorizing.value = false;
  }

  async function createBillingAgreement(): Promise<string> {
    trackPayPalStart();
    isAuthorizing.value = true;

    try {
      return await takeBaToken(options.roundedPrice());
    } catch (error) {
      isAuthorizing.value = false;
      throw error;
    }
  }

  function trackPaymentSuccess(authorizationDuration: number, acceptStartTime: number): void {
    track('Authorization_Success', {
      type: $paymentMethods.paypal,
      price: options.discountPrice(),
    });
    track('Payment duration', {
      type: $paymentMethods.paypal,
      gateway: paypalGatewayType.value,
      authorization_duration: roundNumber(authorizationDuration),
      backend_payment_duration: roundNumber((performance.now() - acceptStartTime) / 1000),
      total_duration: roundNumber((performance.now() - payPalStartTime.value) / 1000),
      result: 'success',
    });
    rememberPaymentMethod('PayPal');
  }

  function showAndReportPaymentError(error: unknown): void {
    openError({ content: 'lp_v7_pop_up_payment_error_message' });

    reportPaymentError({
      error: error as ApiErrorModel,
      stage: options.upsale() ? 'Upsale' : 'Subscription',
      type: $paymentMethods.paypal,
    });
  }

  function trackPaymentErrorDuration(authorizationDuration?: number, acceptStartTime?: number): void {
    track('Payment duration', {
      type: $paymentMethods.paypal,
      gateway: paypalGatewayType.value,
      authorization_duration: authorizationDuration ? roundNumber(authorizationDuration) : undefined,
      backend_payment_duration: acceptStartTime
        ? roundNumber((performance.now() - acceptStartTime) / 1000)
        : undefined,
      total_duration: roundNumber((performance.now() - payPalStartTime.value) / 1000),
      result: 'error',
    });
  }

  function handleAcceptError(error: unknown, authorizationDuration: number, acceptStartTime: number): void {
    trackPaymentErrorDuration(authorizationDuration, acceptStartTime);
    showAndReportPaymentError(error);
  }

  // The email rides the accept response, so the caller needs both halves, not just the failure.
  async function acceptPayPalPayment(billingToken: string): Promise<{ error?: unknown; email?: string }> {
    if (options.upsale()) {
      return {
        error: await buyUpsale({
          paypal_token_id: billingToken,
          item: options.chargebeeId(),
          web_user_uuid: store.userUuid,
          stage: 'upsale',
          amount: options.roundedPrice(),
        }),
      };
    }

    const response = await API.payPalAccept({
      token_id: billingToken,
      item: options.chargebeeId(),
    } as any);

    return { error: response?.detail?.error, email: response?.email };
  }

  async function handleApprove(billingToken: string): Promise<void> {
    isAuthorizing.value = false;
    isLoading.value = true;
    dialogStore.setLoader(true);
    const authorizationDuration = (performance.now() - payPalStartTime.value) / 1000;
    const acceptStartTime = performance.now();

    try {
      // Registers the buyer before the charge; the address itself only exists in the accept response.
      if (options.requestPayerEmail()) {
        await registerPayerWithoutEmail();
      }

      const { error: acceptError, email } = await acceptPayPalPayment(billingToken);

      if (acceptError) {
        handleAcceptError(acceptError, authorizationDuration, acceptStartTime);
        return;
      }

      if (options.requestPayerEmail() && email) {
        await setEmailAfterPpPayment(email);
      }

      trackPaymentSuccess(authorizationDuration, acceptStartTime);
      options.onSuccess();
    } catch (error) {
      handleAcceptError(error, authorizationDuration, acceptStartTime);
    } finally {
      isLoading.value = false;
      dialogStore.setLoader(false);
      dropBaToken();
      void prefetchBaToken();
    }
  }

  function handleCancel(): void {
    isAuthorizing.value = false;
    isLoading.value = false;
    dialogStore.setLoader(false);
  }

  function handleError(error: unknown): void {
    isAuthorizing.value = false;
    isLoading.value = false;

    if (!payPalStartTime.value) {
      reportPaymentInit({
        outcome: 'unavailable',
        action: PAYMENT_INIT_ACTION.PAYPAL,
        attempts: 1,
        error,
      });
      hasInitError.value = true;
      options.onUnavailable();

      return;
    }

    trackPaymentErrorDuration();
    showAndReportPaymentError(error);
  }

  watch(isOnline, (online) => {
    if (!online || !hasInitError.value) return;

    hasInitError.value = false;
    buttonKey.value += 1;
    options.onAvailable();
  });

  return {
    isLoading,
    isAuthorizing,
    buttonKey,
    getClientId,
    handleClick,
    showAuthorizingLoader,
    hideAuthorizingLoader,
    createBillingAgreement,
    prefetchBaToken,
    handleApprove,
    handleCancel,
    handleError,
  };
}
